// Pomodoro timer — records activity to current day's tracks

import React from 'react';
import { CAT_BY_ID } from '../data/categories.js';
import { LineIcon } from './icons.jsx';
import pomoBgUrl from '../assets/pomo-bg.mp3';
import pomoBgCampfireUrl from '../assets/pomo-bg-campfire.mp3';

const AUDIO_OPTIONS = [
  { id: 'default',  label: '音乐',   icon: 'music', url: pomoBgUrl },
  { id: 'campfire', label: '篝火',   icon: 'flame', url: pomoBgCampfireUrl },
  { id: 'mute',     label: '静音',   icon: 'mute', url: null },
];
function getStoredAudioChoice() {
  try { return localStorage.getItem('pomo-audio') || 'default'; } catch (e) { return 'default'; }
}

// 番茄钟自动结束（计时归零）时的一次性提示音。用 Web Audio 合成，无需音频文件。
// 浏览器自动播放策略：用户点「开始」时（手势）创建 / resume AudioContext，几分钟后归零才能发声。
let _chimeCtx = null;
function getChimeCtx() {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_chimeCtx) _chimeCtx = new AC();
  return _chimeCtx;
}
// 在用户手势里调用：暖机解锁，后续非手势时刻才能发声
function warmChimeAudio() {
  try { const c = getChimeCtx(); if (c && c.state === 'suspended') c.resume(); } catch (e) {}
}
function playEndChime() {
  try {
    const ctx = getChimeCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    const now = ctx.currentTime;
    // 轻柔两声「叮—咚」：E6 → A5，各自快起慢落
    [[1318.5, 0], [880, 0.18]].forEach(([freq, t]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = now + t;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.5);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.55);
    });
  } catch (e) { /* 发声失败静默忽略 */ }
}

// 结束提示音可选「短/长语音」文件。用 import.meta.glob 引用：文件未放置时返回空 → 自动回退合成叮咚，
// 不会让构建报错；放进 src/assets/（pomo-end-short.mp3 / pomo-end-long.mp3）后 HMR 自动识别，singlefile 打包也内联。
// 匹配 src/assets/pomo-end-short.mp3 / pomo-end-long.mp3（已放置）
const _endVoiceFiles = import.meta.glob('../assets/pomo-end-*.mp3', { eager: true, query: '?url', import: 'default' });
function endVoiceUrl(name) { // name: 'short' | 'long'
  const k = Object.keys(_endVoiceFiles).find(key => key.includes(`pomo-end-${name}`));
  return k ? _endVoiceFiles[k] : null;
}
const END_SOUND_OPTIONS = [
  { id: 'ding',  label: '叮', title: '结束音：叮咚（合成）' },
  { id: 'short', label: '短', title: '结束音：短语音' },
  { id: 'long',  label: '长', title: '结束音：长语音' },
];
function getStoredEndSound() {
  try { return localStorage.getItem('pomo-end-sound') || 'ding'; } catch (e) { return 'ding'; }
}
// 番茄钟自动归零时按所选播放：叮咚=合成；短/长=语音文件（缺文件或播放失败则回退叮咚）
function playEndSound() {
  const choice = getStoredEndSound();
  if (choice === 'short' || choice === 'long') {
    const url = endVoiceUrl(choice);
    if (url) {
      try { const a = new Audio(url); a.play().catch(() => playEndChime()); return; } catch (e) { /* 回退 */ }
    }
  }
  playEndChime();
}

function Pomodoro({ currentCat, state, actions, pomoLogs = [], onApplyPomoLogs, audioChoice, onAudioChoice, onEditCat, onSelectedCat }) {
  const { mode, duration, remaining, runningStartedAt, runningPauses, runningManualPauseStartedAt } = state;
  const { setMode, setDuration, setRemaining, reset, delay, completeEarly, startFresh } = actions;
  const [selectMode, setSelectMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  const [endSound, setEndSound] = React.useState(getStoredEndSound);
  function chooseEndSound(id) {
    warmChimeAudio(); // 借这次点击（用户手势）暖机解锁，保证后续归零能发声
    setEndSound(id);
    try { localStorage.setItem('pomo-end-sound', id); } catch (e) {}
  }

  // 背景音频已上提到 App 顶层 <PomodoroAudio>（常驻，不随 tab 卸载，切视图也不打断）。
  // 这里只保留音色切换 UI，状态由 props.audioChoice / onAudioChoice 驱动。

  const min = Math.floor(remaining/60), sec = remaining%60;
  const progress = 1 - (remaining / (duration*60));
  const r = 70;
  const cir = 2 * Math.PI * r;
  const cat = CAT_BY_ID[currentCat] || { label: '未分类', color: 'var(--text-dim)' };
  const statusLabel = mode === 'idle' ? '已选择' : mode === 'running' ? '正在记录' : mode === 'paused' ? '已暂停' : '刚完成';
  // 接力下拉（N47）：点「结束」→ 选去怦然/去放松 → 结束当前 + 立刻开 10min 新番茄（分类=选的）
  // 选项写死两项，留扩展（用户 2026-06-08：以后再加更多）
  const HANDOFF_OPTS = [
    { catId: 'prep_pr', label: '去怦然' },
    { catId: 'fun_relax', label: '去放松' },
  ];
  const [handoffOpen, setHandoffOpen] = React.useState(false);
  const handoffRef = React.useRef(null);
  React.useEffect(() => {
    if (!handoffOpen) return;
    const onDown = (e) => { if (handoffRef.current && !handoffRef.current.contains(e.target)) setHandoffOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [handoffOpen]);
  function goHandoff(catId) {
    setHandoffOpen(false);
    if (mode === 'running' || mode === 'paused') completeEarly(); // 落库当前（用旧分类），再切分类
    if (onSelectedCat) onSelectedCat(catId);
    startFresh(10);
  }

  function formatDur(durMin) {
    if (durMin >= 60 && durMin % 60 === 0) return `${durMin / 60}h`;
    if (durMin >= 60) return `${Math.floor(durMin / 60)}h ${durMin % 60}m`;
    return `${durMin}m`;
  }
  // 直接读 log 里存的 startMs 和 endMs，不再用 endMs - durMin 逆推
  // （之前逆推会让电脑睡眠等暂停事件污染显示）
  function formatTimeRange(startMs, endMs) {
    const fmt = ms => {
      const d = new Date(ms);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    return `${fmt(startMs)}-${fmt(endMs)}`;
  }
  function formatHMS(ms) {
    const d = new Date(ms);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  }
  function formatPauseTooltip(pauses) {
    if (!pauses || pauses.length === 0) return '';
    return pauses.map((p, i) => {
      const sec = Math.max(1, Math.round((p.resumedAt - p.at) / 1000));
      const kind = p.kind === 'sleep' ? '休眠' : '手动暂停';
      return `${i + 1}. ${kind} ${formatHMS(p.at)} → ${formatHMS(p.resumedAt)}（${sec}s）`;
    }).join('\n');
  }

  const sortedLogs = [...pomoLogs].sort((a, b) => b.endMs - a.endMs);
  const visibleLogs = sortedLogs.slice(0, 4);
  const unappliedLogs = sortedLogs.filter(l => !l.applied);
  const hasUnapplied = unappliedLogs.length > 0;

  function toggleSelect(id) {
    setSelectedIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function applyAll() {
    if (!onApplyPomoLogs) return;
    onApplyPomoLogs(unappliedLogs.map(l => l.id));
  }
  function applySelected() {
    if (!onApplyPomoLogs || selectedIds.size === 0) return;
    onApplyPomoLogs([...selectedIds]);
    setSelectedIds(new Set());
    setSelectMode(false);
  }

  return (
    <div className="pomodoro">
      <div className="pom-title">
        {/* 按钮组左对齐（仿时间分类左折叠按钮），「番茄钟」文字跟后（仿时间分类标题） */}
        <span className="pom-audio-switch" role="group" aria-label="背景音切换">
          {AUDIO_OPTIONS.map(opt => (
            <button
              key={opt.id}
              type="button"
              className={`pom-audio-opt ${audioChoice === opt.id ? 'on' : ''}`}
              onClick={() => onAudioChoice && onAudioChoice(opt.id)}
              title={`背景音：${opt.label}`}
            ><LineIcon name={opt.icon} size={15} /></button>
          ))}
        </span>
        <span className="pom-audio-switch pom-end-switch" role="group" aria-label="结束提示音切换">
          {END_SOUND_OPTIONS.map(opt => (
            <button
              key={opt.id}
              type="button"
              className={`pom-audio-opt pom-end-opt ${endSound === opt.id ? 'on' : ''}`}
              onClick={() => chooseEndSound(opt.id)}
              title={opt.title}
            >{opt.label}</button>
          ))}
        </span>
        🦀
      </div>
      <div className="pom-sub">{statusLabel}：<span className="pom-sub-cat" style={{ color: cat.color }}>{cat.label}</span>
        {onEditCat && (
          <button type="button" className="pom-edit-cat" onClick={onEditCat} title="切换时间分类：清掉日表选区后去分类面板选，不影响已选中的时间块" aria-label="切换时间分类">切换</button>
        )}
      </div>
      <div className="pom-clock">
        <svg width="180" height="180" viewBox="0 0 180 180">
          <circle cx="90" cy="90" r={r} stroke="var(--card-border)" strokeWidth="6" fill="none" />
          <circle cx="90" cy="90" r={r}
            stroke={cat.color} strokeWidth="6" fill="none"
            strokeDasharray={cir} strokeDashoffset={cir * (1-progress)}
            transform="rotate(-90 90 90)" strokeLinecap="round"
          />
          <text x="90" y="92" textAnchor="middle" fontSize="32" fontWeight="600" fill="var(--text)">
            {String(min).padStart(2,'0')}:{String(sec).padStart(2,'0')}
          </text>
          <text x="90" y="115" textAnchor="middle" fontSize="11" fill="var(--text-dim)">
            {mode==='idle' && '准备开始'}
            {mode==='running' && '专注中…'}
            {mode==='paused' && '已暂停'}
            {mode==='break' && '休息一下'}
          </text>
        </svg>
      </div>
      <div className="pom-duration">
        {[15, 25, 45, 60].map(d => (
          <button key={d} className={`pom-len ${duration===d?'active':''}`} onClick={() => { setDuration(d); setRemaining(d*60); setMode('idle'); }}>{d}m</button>
        ))}
      </div>
      <div className="pom-actions">
        {mode === 'idle' && <button className="btn primary" onClick={() => { warmChimeAudio(); setMode('running'); }}>开始</button>}
        {mode === 'running' && <button className="btn" onClick={() => setMode('paused')}>暂停</button>}
        {mode === 'paused' && <button className="btn primary" onClick={() => setMode('running')}>继续</button>}
        {(mode === 'running' || mode === 'paused') && (
          <button className="btn" onClick={delay} title="把开始时间重置到现在（之前点开始但没真做事时用）">延迟</button>
        )}
        {mode === 'break' && <button className="btn primary" onClick={reset}>重置</button>}
        {(mode !== 'idle') && (
          <div className="pom-handoff" ref={handoffRef}>
            <button className="btn" onClick={() => setHandoffOpen(o => !o)} aria-haspopup="true" aria-expanded={handoffOpen}>结束 ▾</button>
            {handoffOpen && (
              <div className="pom-handoff-menu" role="menu">
                {HANDOFF_OPTS.map(o => (
                  <button key={o.catId} type="button" className="pom-handoff-item" role="menuitem"
                    onClick={() => goHandoff(o.catId)}>{o.label}</button>
                ))}
              </div>
            )}
          </div>
        )}
        {(mode === 'running' || mode === 'paused') && (
          <button className="btn" onClick={completeEarly} title="按实际已专注分钟数记录后结束（不按预设的 15m/25m）">提前</button>
        )}
      </div>
      {(mode === 'running' || mode === 'paused') && runningStartedAt && (
        <div className="pom-current">
          <div className="pom-current-title">
            正在进行
            <span className="pom-current-mode">{mode === 'paused' ? '· 已暂停' : '· 专注中'}</span>
          </div>
          <div className="pom-current-row">
            <span className="pom-current-time">{formatHMS(runningStartedAt)} → 进行中</span>
            <span className="pom-current-cat" style={{ color: cat.color }}>{cat.label}</span>
            <span className="pom-current-dur">{formatDur(duration)}</span>
          </div>
          {(runningPauses.length > 0 || (mode === 'paused' && runningManualPauseStartedAt)) && (
            <div className="pom-current-pauses">
              {(() => {
                // 把每条 pause 拆成事件列表，按 at 升序展示（像计算器历史）。
                // manual：拆成「暂停」+「继续」两条事件
                // sleep：一条事件（自动检测的，没有"开始"动作）
                // 当前正在进行的手动暂停（mode === 'paused'）：补一条「暂停」+ 待续提示
                const events = [];
                runningPauses.forEach((p, idx) => {
                  const sec = Math.max(1, Math.round((p.resumedAt - p.at) / 1000));
                  if (p.kind === 'sleep') {
                    events.push({
                      at: p.at,
                      kind: 'sleep',
                      label: '系统休眠（计入有效时间）',
                      icon: '💤',
                      time: `${formatHMS(p.at)} → ${formatHMS(p.resumedAt)}`,
                      sec,
                      key: `s-${idx}`,
                    });
                  } else {
                    events.push({
                      at: p.at,
                      kind: 'manual-pause',
                      label: '暂停',
                      icon: '⏸',
                      time: formatHMS(p.at),
                      key: `mp-${idx}`,
                    });
                    events.push({
                      at: p.resumedAt,
                      kind: 'manual-resume',
                      label: '继续',
                      icon: '▶',
                      time: formatHMS(p.resumedAt),
                      sec,
                      key: `mr-${idx}`,
                    });
                  }
                });
                if (mode === 'paused' && runningManualPauseStartedAt) {
                  events.push({
                    at: runningManualPauseStartedAt,
                    kind: 'manual-pause-ongoing',
                    label: '暂停',
                    icon: '⏸',
                    time: `${formatHMS(runningManualPauseStartedAt)} · 等待"继续"…`,
                    key: 'mp-ongoing',
                  });
                }
                events.sort((a, b) => a.at - b.at);
                return events.map(e => (
                  <div key={e.key} className={`pom-current-pause-item kind-${e.kind}`}>
                    <span className="pom-current-pause-icon">{e.icon}</span>
                    <span className="pom-current-pause-kind">{e.label}</span>
                    <span className="pom-current-pause-time">{e.time}</span>
                    {e.sec != null && <span className="pom-current-pause-sec">{e.sec >= 60 ? `${Math.floor(e.sec/60)}m${e.sec%60}s` : `${e.sec}s`}</span>}
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}
      {sortedLogs.length > 0 && (
        <div className="pom-logs">
          <div className="pom-logs-title">今日完成 {sortedLogs.length} 个</div>
          {visibleLogs.map((l) => {
            const c = CAT_BY_ID[l.catId];
            const catName = (c && (c.name || c.label)) || '未分类';
            const checkable = selectMode && !l.applied;
            // 展示前合并相邻 sleep（≤ 2000ms 视为同段），兜住没有 record-time merge 的旧数据
            const pauses = [];
            (l.pauses || []).forEach(p => {
              if (p.kind === 'sleep' && pauses.length > 0) {
                const last = pauses[pauses.length - 1];
                if (last.kind === 'sleep' && (p.at - last.resumedAt) <= 2000) {
                  pauses[pauses.length - 1] = { ...last, resumedAt: p.resumedAt };
                  return;
                }
              }
              pauses.push(p);
            });
            return (
              <div key={l.id} className={`pom-log ${l.applied ? 'applied' : ''}`}>
                {checkable && (
                  <input
                    type="checkbox"
                    className="pom-log-check"
                    checked={selectedIds.has(l.id)}
                    onChange={() => toggleSelect(l.id)}
                  />
                )}
                <span className="pom-log-time">{formatTimeRange(l.startMs, l.endMs)}</span>
                <span className="pom-log-cat">{catName}</span>
                <span className="pom-log-dur">{formatDur(l.durationMin)}</span>
                {pauses.length > 0 && (
                  <span className="pom-log-pauses" title={formatPauseTooltip(pauses)}>
                    ⏸ {pauses.length}
                  </span>
                )}
                {l.applied && <span className="pom-log-applied" title="已入轴">✓</span>}
              </div>
            );
          })}
          {hasUnapplied && !selectMode && (
            <div className="pom-apply-actions">
              <button className="btn primary" onClick={applyAll}>一键全部入轴</button>
              <button className="btn" onClick={() => setSelectMode(true)}>选择部分入轴</button>
            </div>
          )}
          {selectMode && (
            <div className="pom-apply-actions">
              <button className="btn primary" disabled={selectedIds.size === 0} onClick={applySelected}>
                确认入轴{selectedIds.size > 0 ? ` ${selectedIds.size} 条` : ''}
              </button>
              <button className="btn" onClick={() => { setSelectMode(false); setSelectedIds(new Set()); }}>取消</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Hook lifted to App level so the timer keeps running across tab switches
// onComplete(entry) 由 App 持久化到 day.pomoLogs
// entry 字段：{ startMs, endMs, durationMin, durationSec(wall time), pauses[], catId }
//   - startMs:  真正开始时间（点开始的瞬间，不变）
//   - endMs:    真正结束时间（remaining 归零的瞬间）
//   - durationSec: 墙钟时长 = (endMs - startMs) / 1000
//   - pauses[]: { at, resumedAt, kind: 'manual'|'sleep' }
//        manual: 用户点了暂停 / 继续（这段时间不计入有效番茄时间）
//        sleep:  setInterval tick 间隔 > 3s 自动判定为系统睡眠 / 浏览器节流
//                （这段**算入有效番茄时间** —— 用户去做该专注的事了，仅记录用于透明展示）
//
// 计时逻辑：remaining 用墙钟时间反推，不是每秒 -1
//   effective_elapsed = (now - startedAt) - 累计手动暂停时长
//   remaining = duration*60 - effective_elapsed
//   系统睡眠不扣 —— 自动归入有效时间
function usePomodoroState(currentCatRef, onComplete) {
  const [mode, setMode] = React.useState('idle');
  const [duration, setDuration] = React.useState(25);
  const [remaining, setRemaining] = React.useState(25 * 60);
  const [runningStartedAt, setRunningStartedAtState] = React.useState(null);
  const [runningPauses, setRunningPausesState] = React.useState([]);
  const [runningManualPauseStartedAt, setRunningManualPauseStartedAtState] = React.useState(null);

  // refs（避免闭包过期 + 不触发额外 re-render）
  const startedAtRef = React.useRef(null);
  const pausesRef = React.useRef([]);
  const lastTickAtRef = React.useRef(null);
  const manualPauseStartedAtRef = React.useRef(null);
  const manualPauseAccumulatedMsRef = React.useRef(0);  // 累计已 resume 的手动暂停 ms
  const lastModeRef = React.useRef('idle');
  const tickRef = React.useRef();

  // refs + state 同步小工具
  function setStartedAt(v) {
    startedAtRef.current = v;
    setRunningStartedAtState(v);
  }
  // 合并阈值：相邻两条 sleep 间隔 ≤ 2000ms 视为同一段系统休眠（浏览器 background throttle 会
  // 每分钟补一次 tick，每次都被判为独立 sleep，本质是一段连续休眠）
  function pushPause(p) {
    if (p.kind === 'sleep' && pausesRef.current.length > 0) {
      const last = pausesRef.current[pausesRef.current.length - 1];
      if (last.kind === 'sleep' && (p.at - last.resumedAt) <= 2000) {
        const merged = { ...last, resumedAt: p.resumedAt };
        pausesRef.current = [...pausesRef.current.slice(0, -1), merged];
        setRunningPausesState(pausesRef.current);
        return;
      }
    }
    pausesRef.current = [...pausesRef.current, p];
    setRunningPausesState(pausesRef.current);
  }
  function clearAll() {
    startedAtRef.current = null;
    pausesRef.current = [];
    lastTickAtRef.current = null;
    manualPauseStartedAtRef.current = null;
    manualPauseAccumulatedMsRef.current = 0;
    setRunningStartedAtState(null);
    setRunningPausesState([]);
    setRunningManualPauseStartedAtState(null);
  }

  // 追踪 mode 转移，识别"手动暂停"段（resume 时记录 + 累加到 manualPauseAccumulatedMs）
  React.useEffect(() => {
    const prev = lastModeRef.current;
    if (prev === 'running' && mode === 'paused') {
      const at = Date.now();
      manualPauseStartedAtRef.current = at;
      setRunningManualPauseStartedAtState(at);
    } else if (prev === 'paused' && mode === 'running') {
      if (manualPauseStartedAtRef.current) {
        const at = manualPauseStartedAtRef.current;
        const resumedAt = Date.now();
        manualPauseAccumulatedMsRef.current += (resumedAt - at);
        pushPause({ at, resumedAt, kind: 'manual' });
        manualPauseStartedAtRef.current = null;
        setRunningManualPauseStartedAtState(null);
      }
    }
    lastModeRef.current = mode;
  }, [mode]);

  React.useEffect(() => {
    if (mode === 'running') {
      // 首次进入 running（点"开始"）：锚定真正开始时间，清空之前的 pauses
      if (!startedAtRef.current) {
        setStartedAt(Date.now());
        pausesRef.current = [];
        setRunningPausesState([]);
        manualPauseAccumulatedMsRef.current = 0;
      }
      // 每次进入 running（包括"继续"后）都重置 lastTick，
      // 否则继续后的第一个 tick 会把暂停那段误识为"睡眠"
      lastTickAtRef.current = Date.now();

      tickRef.current = setInterval(() => {
        const now = Date.now();
        const lastTick = lastTickAtRef.current;
        if (lastTick && (now - lastTick) > 3000) {
          // 系统睡眠 / 浏览器节流：记录但**不**扣有效时间（用户去做番茄事项了）
          pushPause({
            at: lastTick + 1000,
            resumedAt: now,
            kind: 'sleep',
          });
        }
        lastTickAtRef.current = now;

        const startedAt = startedAtRef.current;
        if (!startedAt) return;

        // 墙钟时间反推 remaining
        const elapsed = now - startedAt;
        const manualPaused = manualPauseAccumulatedMsRef.current;
        const effectiveElapsed = elapsed - manualPaused;  // sleep 不减
        const newRemaining = Math.max(0, Math.ceil((duration * 60 * 1000 - effectiveElapsed) / 1000));
        setRemaining(newRemaining);

        if (newRemaining <= 0) {
          clearInterval(tickRef.current);
          const cur = (currentCatRef && currentCatRef.current) || null;
          const endMs = now;
          const durationSec = Math.round((endMs - startedAt) / 1000);
          const pausesSnap = [...pausesRef.current];
          if (onComplete) onComplete({
            startMs: startedAt, endMs,
            durationMin: duration,
            durationSec,
            pauses: pausesSnap,
            catId: cur || null,
          });
          clearAll();
          setMode('break');
          playEndSound(); // 仅自动归零时响；提前结束(completeEarly)不响
        }
      }, 1000);
      return () => clearInterval(tickRef.current);
    }
  }, [mode, duration]);

  function reset() {
    setRemaining(duration * 60);
    setMode('idle');
    clearAll();
  }
  // 提前：还没到设定时长就主动结束，按实际有效专注分钟数记录（不按预设档）。
  // durationMin = round((endMs - startMs - 累计手动暂停) / 60s)，至少 1 分钟。
  function completeEarly() {
    if (!startedAtRef.current) return;
    const now = Date.now();
    // 如果正在 paused，先把这段手动暂停闭合记录（resumedAt = now）
    if (manualPauseStartedAtRef.current) {
      const at = manualPauseStartedAtRef.current;
      pausesRef.current = [...pausesRef.current, { at, resumedAt: now, kind: 'manual' }];
      manualPauseAccumulatedMsRef.current += (now - at);
      manualPauseStartedAtRef.current = null;
    }
    const startMs = startedAtRef.current;
    const endMs = now;
    const wallMs = endMs - startMs;
    const manualMs = manualPauseAccumulatedMsRef.current;
    const effectiveMs = Math.max(0, wallMs - manualMs);
    const actualMin = Math.max(1, Math.round(effectiveMs / 60000));
    const pausesSnap = [...pausesRef.current];
    const cur = (currentCatRef && currentCatRef.current) || null;
    clearInterval(tickRef.current);
    if (onComplete) onComplete({
      startMs, endMs,
      durationMin: actualMin,
      durationSec: Math.round(wallMs / 1000),
      pauses: pausesSnap,
      catId: cur || null,
    });
    clearAll();
    setMode('break');
  }
  // 延迟：用户点开始后没真做事，现在才真正开始 —— 重置开始时间到 now，清空之前的 pauses，
  // 倒计时从 duration 重新跑。当前若处于 paused 状态也直接转 running。
  function delay() {
    const now = Date.now();
    startedAtRef.current = now;
    pausesRef.current = [];
    lastTickAtRef.current = now;
    manualPauseStartedAtRef.current = null;
    manualPauseAccumulatedMsRef.current = 0;
    setRunningStartedAtState(now);
    setRunningPausesState([]);
    setRunningManualPauseStartedAtState(null);
    setRemaining(duration * 60);
    if (mode !== 'running') setMode('running');
  }
  // 接力（N47）：原子地"结束当前→立刻开新 minutes 分钟番茄"。仿 delay 但带 setDuration。
  // 新番茄分类由外部 onSelectedCat 切换（落库读 selectedCatRef）。
  function startFresh(minutes) {
    const now = Date.now();
    startedAtRef.current = now;
    pausesRef.current = [];
    lastTickAtRef.current = now;
    manualPauseStartedAtRef.current = null;
    manualPauseAccumulatedMsRef.current = 0;
    setRunningStartedAtState(now);
    setRunningPausesState([]);
    setRunningManualPauseStartedAtState(null);
    setDuration(minutes);
    setRemaining(minutes * 60);
    setMode('running');
  }
  return {
    state: { mode, duration, remaining, runningStartedAt, runningPauses, runningManualPauseStartedAt },
    actions: { setMode, setDuration, setRemaining, reset, delay, completeEarly, startFresh },
  };
}

// 音色选择 + localStorage 持久化，上提到 App 共享（番茄面板的切换按钮 与 常驻音频 用同一份）
function usePomodoroAudioChoice() {
  const [audioChoice, setAudioChoice] = React.useState(getStoredAudioChoice);
  React.useEffect(() => {
    try { localStorage.setItem('pomo-audio', audioChoice); } catch (e) {}
  }, [audioChoice]);
  return [audioChoice, setAudioChoice];
}

// 背景音常驻组件：挂在 App 顶层、不受 view/tab 切换影响，只跟 mode 走。
// 切到月度/画册/数据洞察时音乐继续；番茄结束/重置才停。
function PomodoroAudio({ mode, runningStartedAt, audioChoice }) {
  const audioUrl = (AUDIO_OPTIONS.find(o => o.id === audioChoice) || AUDIO_OPTIONS[0]).url;
  const audioRef = React.useRef(null);
  const prevStartedAtRef = React.useRef(null);
  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (mode === 'running' && audioUrl) {
      if (runningStartedAt !== prevStartedAtRef.current) audio.currentTime = 0; // 新开始/延迟 → 从头播
      audio.play().catch(() => {/* 浏览器拒绝自动播放就静默忽略 */});
    } else if (mode === 'paused') {
      audio.pause();
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
    prevStartedAtRef.current = runningStartedAt;
  }, [mode, runningStartedAt, audioUrl]);
  if (!audioUrl) return null; // 静音：不渲染 audio 元素（切到静音时上一个元素卸载即停声）
  return <audio ref={audioRef} src={audioUrl} loop preload="auto" />;
}

export { Pomodoro, PomodoroAudio, usePomodoroState, usePomodoroAudioChoice };
